import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Icons,
  iconSize,
  Link,
  palette,
  Stack,
  StatusChip,
  Typography,
  type StatusKind,
} from '@munifocus/ui';
import type { ContactChannel, ContactLogEntryView, ContactOutcome } from '@munifocus/contracts-gov';
import { formatDateTime } from '../../lib/relative-time';

const CHANNEL_ICON: Record<ContactChannel, typeof Icons.Phone> = {
  phone: Icons.Phone,
  walk_in: Icons.User,
  email: Icons.Mail,
};

const OUTCOME_KIND: Record<ContactOutcome, StatusKind> = {
  resolved: 'success',
  callback: 'pending',
  referred: 'neutral',
  file_opened: 'approved',
  attached: 'approved',
  sent_to_triage: 'neutral',
};

/** Who called, as this reader may see it. */
export function callerLabel(entry: ContactLogEntryView, t: (key: string) => string): string {
  if (entry.redacted) return t('gov.frontDesk.withheld');
  if (entry.anonymous && !entry.callerName) return t('gov.frontDesk.anonymous');
  const name = entry.callerName?.trim() || t('gov.frontDesk.unnamedCaller');
  return entry.anonymous ? `${name} (${t('gov.frontDesk.anonymous')})` : name;
}

/** One log entry: channel, caller, topic, property, notes, files, outcome. */
export function ContactEntrySummary({
  entry,
  actions,
  highlight,
}: {
  entry: ContactLogEntryView;
  actions?: ReactNode;
  /** Draws the overdue rule down the left edge. */
  highlight?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const ChannelIcon = CHANNEL_ICON[entry.channel] ?? Icons.Phone;
  const contact = entry.redacted
    ? null
    : [entry.callerPhone, entry.callerEmail].filter(Boolean).join(' · ');
  return (
    <Box
      component="article"
      aria-label={`${t(`gov.frontDesk.channel.${entry.channel}`)} — ${callerLabel(entry, t)}`}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${palette.border.line}`,
        borderLeft: highlight ? `4px solid ${palette.semantic.emergency}` : undefined,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', gap: 1, alignItems: { sm: 'flex-start' } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <ChannelIcon size={iconSize.sm} aria-hidden />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {callerLabel(entry, t)}
            </Typography>
            <Typography variant="caption" sx={{ color: palette.text.muted }}>
              {t(`gov.frontDesk.topic.${entry.topic}`, { defaultValue: entry.topic })}
            </Typography>
          </Stack>
          {contact && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {contact}
            </Typography>
          )}
          {(entry.address || entry.pid) && (
            <Typography variant="caption" sx={{ display: 'block', color: palette.text.muted }}>
              {[entry.address, entry.pid ? t('gov.frontDesk.pidShort', { pid: entry.pid }) : null]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
          {entry.notes && (
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {entry.notes}
            </Typography>
          )}
          {entry.files.length > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {t('gov.frontDesk.linkedFiles')}{' '}
              {entry.files.map((f, i) => (
                <span key={f.id}>
                  {i > 0 && ', '}
                  {f.redacted ? (
                    f.reference
                  ) : (
                    <Link component={RouterLink} to={`/files/${f.id}`} underline="hover">
                      {f.reference}
                    </Link>
                  )}
                </span>
              ))}
            </Typography>
          )}
          {entry.callback && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                color: entry.callback.overdue ? palette.semantic.emergency : undefined,
                fontWeight: entry.callback.overdue ? 700 : undefined,
              }}
            >
              {entry.callback.doneAt
                ? t('gov.frontDesk.callback.doneLine', {
                    name: entry.callback.assigneeName,
                    at: formatDateTime(entry.callback.doneAt, i18n.language),
                  })
                : t(
                    entry.callback.overdue
                      ? 'gov.frontDesk.callback.overdueLine'
                      : 'gov.frontDesk.callback.dueLine',
                    {
                      name: entry.callback.assigneeName,
                      at: formatDateTime(entry.callback.dueAt, i18n.language),
                    },
                  )}
            </Typography>
          )}
          {/* What was said when the callback was returned: saved with Mark
              done, and the one part of the call anyone else will ask about. */}
          {entry.callback?.doneAt && entry.callback.doneNote && (
            <Box
              sx={{
                mt: 0.75,
                pl: 1.25,
                borderLeft: `2px solid ${palette.border.line}`,
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', color: palette.text.muted }}>
                {t('gov.frontDesk.callback.doneNoteLabel')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {entry.callback.doneNote}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" sx={{ display: 'block', color: palette.text.muted }}>
            {t('gov.frontDesk.loggedBy', {
              name: entry.createdByName,
              at: formatDateTime(entry.createdAt, i18n.language),
            })}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', gap: 1 }}
        >
          <StatusChip
            status={OUTCOME_KIND[entry.outcome] ?? 'neutral'}
            label={t(`gov.frontDesk.outcome.${entry.outcome}`)}
          />
          {actions}
        </Stack>
      </Stack>
    </Box>
  );
}
